<template lang="pug">
#waiter-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        b-spinner.spinner.mr-2(v-if="waiter == null")
        span(v-else-if="waiter.name") {{ waiter.name }}
        span(v-else).warning-message 未入力
        span 様の情報変更

    .box-body.edit-body
      .message-container(v-if="!existRoomType")
        .warning-message
          div 本施設、グループ施設に申込可能な部屋が設定されていないため、登録できません。
          div 設定をご確認ください。

      .template-container(v-if="waiterLoaded && existRoomType")
        .information.mb-2(v-if="waiter.waiter.admissionApplication.information" style="white-space: pre-wrap; word-wrap: break-word;")
          span {{ waiter.waiter.admissionApplication.information }}

        .template(v-for="(property, index) in waiter.waiter.admissionApplication.templateAnswer.properties")
          WaiterTemplateInputItem(v-model="waiter.waiter.admissionApplication.templateAnswer.properties[index]"
            :waiterViewableFacilitiesMap="waiterViewableFacilitiesMap" :isChaild="false")

        .created
          .d-flex.flex-row.align-items-center
            .title.mr-1 申込日
            span.validation-label.mr-1 必須
          .d-flex.flex-row.align-items-center.p-3
            b-form-select.year(:id="'created-year'" v-model="admissionApplicationCreated.year" :name="'created-year'"
              :options="getYearOptions(admissionApplicationCreated)"
              :class="[ { noinput: !admissionApplicationCreated.year } ]" v-validate="{ required: true }" data-vv-as="年")
            span.ml-2.mr-2 年
            b-form-select.month(:id="'created-month'" v-model="admissionApplicationCreated.month" :name="'created-month'" :options="getMonthOptions()"
              :class="[ { noinput: !admissionApplicationCreated.month } ]" v-validate="{ required: true }" data-vv-as="月")
            span.ml-2.mr-2 月
            b-form-select.date(:id="'created-date'" v-model="admissionApplicationCreated.date" :name="'created-date'" :options="getDateOptions()"
              :class="[ { noinput: !admissionApplicationCreated.date } ]" v-validate="{ required: true }" data-vv-as="日")
            span.ml-2.mr-2 日
          .error-messages(v-for="error in errors.collect('created-year')")
            .error-message {{ error }}
          .error-messages(v-for="error in errors.collect('created-month')")
            .error-message {{ error }}
          .error-messages(v-for="error in errors.collect('created-date')")
            .error-message {{ error }}
          .error-messages(v-for="error in errors.collect('created')")
            .error-message {{ error }}

        .labels
          .title ラベル
          .label.mb-3.ml-3(v-for="label in labels")
            b-form-checkbox(v-if="label.use" :id="'label-' + label.id" v-model="label.checked" :name="'label-' + label.id" :value="true" :unchecked-value="false")
              span.waiter-label.mr-2(:style="{ 'color': label.color, 'background-color': label.bgColor }") {{ label.name }}
              span {{ label.description }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(v-if="waiterLoaded && existRoomType && waiter.status == 8" @click="showConfirmModal(waiter.status, 'temp')" type="button") 一時保存
        button.btn.update-btn(v-if="waiterLoaded && existRoomType && waiter.status == 8" @click="showConfirmModal(waiter.status, 'register')" type="button") 登録
        button.btn.update-btn(v-else-if="existRoomType" @click="showConfirmModal(waiter.status, 'update')" type="button") 変更
        button.btn.cancel-btn(@click="gotoWaiterDetail" type="button") キャンセル

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

  OkCancelModal(v-model="showUpdateModal" :title="updateModalTitle" @ok="updateWaiter")
  OkModal(v-model="showUpdateSuccessModal" :title="successModalTitle" @ok="gotoWaiterDetail")

  //- file upload error modal
  FileUploadErrorModal(v-model="showUploadErrorModal")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  ProgressLoading(v-model="currentUploadCount" :max="uploadCount" :isBusy="progressLoading" :message="progressMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { WaiterListState, SendWaiterMessagesState } from "@/store/NeedRefreshes";
import { PcUrl } from "@/commons/constants/PcUrl";
import Setting from "../../../../public/setting.json";

import CustomValidate from "@/commons/utils/CustomValidate";
import LoginUser from "@/store/LoginUser";
import SystemError from "@/commons/models/SystemError";

import {
  RoomType,
  TemplateItem,
  TemplateItemAddress,
  TemplateItemBirthday,
  TemplateItemCheckbox,
  TemplateItemFile,
  TemplateItemGroup,
  TemplateItemInput,
  TemplateItemMultipleCheckbox,
  TemplateItemName,
  TemplateItemRadio,
  TemplateItemRadioGroup,
  TemplateItemRoomType,
  TemplateItemSection,
  TemplateItemSelect,
  TemplateItemSwitchCheckbox,
  TemplateItemTel,
  TemplateItemText,
  TemplateItemTextarea,
  TemplateItemYearMonth,
  TemplateItemYearMonthDate,
  TemplateType,
  YearMonthDateAnswer
} from "@/commons/services/AdmissionApplicationTemplates";
import FacilitiesService, { CustomRecruitmentRoom, FacilityGetModel } from "@/commons/services/Facilities";
import WaitersService, { UpdateType, WaitersGetModel, WaitersPatchModel } from "@/commons/services/Waiters";
import S3UrlService from "@/commons/services/S3Url";
import S3LocationsService from "@/commons/services/S3Locations";
import { S3LocationRequest, S3LocationResponse, S3LocationType } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";
import WaiterLabelsService, { WaiterLabelsGetModel } from "@/commons/services/WaiterLabels";

import FileUploadErrorModal from "@/pc/basics/FileUploadErrorModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ProgressLoading from "@/pc/basics/ProgressLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import WaiterTemplateInputItem from "@/pc/containers/waiters/WaiterTemplateInputItem.vue";
import { ResidentStatus } from "@/commons/services/Residents";

/**
 * ### 待機者編集画面コンポーネント
 */
@Component({
  components: {
    FileUploadErrorModal,
    OkCancelModal,
    OkModal,
    PageLoading,
    ProgressLoading,
    ValidationAlert,
    WaiterTemplateInputItem
  },
  $_veeValidate: { validator: "new" }
})
export default class WaiterEdit extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 待機者サービス */
  @Inject("WaitersService") waitersService!: WaitersService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;
  /** 待機者ラベルサービス */
  @Inject("WaiterLabelsService") waiterLabelsService!: WaiterLabelsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新確認モーダルタイトル */
  updateModalTitle: string = "";
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;
  /** 更新完了モーダルタイトル */
  successModalTitle: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 現在のファイルアップロード数 */
  currentUploadCount: number = 0;
  /** ファイルアップロード総数 */
  uploadCount: number = 0;
  /** プログレスモーダルメッセージ */
  progressMessage: string = "";
  /** プログレスモーダル表示可否 */
  progressLoading: boolean = false;

  /** 閲覧可能施設MAP(key:施設ID、value:施設情報) */
  waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();

  /** 部屋種類が存在するか否か */
  existRoomType: boolean = true;

  /** 更新対象の待機者 */
  waiter: WaitersGetModel | null = null;
  /** 一時保存ボタンの押下か否か */
  isTemporarilyButton: boolean = false;
  /** 待機者情報が全て読み込まれたか否か */
  waiterLoaded: boolean = false;
  /** 利用者状態 */
  residentStatus: ResidentStatus = ResidentStatus.UNKNOWN;
  /** 申込日（プロパティ名はcreatedだが問題無し）reisident.waiter.admissionApplication.createdの値を指す */
  admissionApplicationCreated: YearMonthDateAnswer = {
    year: "",
    month: "",
    date: ""
  };
  /** 更新種別 */
  updateType: string = "";
  /** ラベル */
  labels: Array<WaiterLabel> = [];

  /** 確認モーダル用エラーが存在するか */
  validInputError: boolean = true;

  /** エラーメッセージ */
  errorMessage: string = "";
  /** ファイルアップロードエラーモーダル表示可否 */
  showUploadErrorModal: boolean = false;

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 部屋種類の表示対象の閲覧可能施設を取得し、MAP化する
   * - 待機者を取得する
   * - S3にアップロードされているファイルを復元する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const waiterViewableFacilities: Array<FacilityGetModel> = await this.getWaiterViewableFacilities(userStore);
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = this.createWaiterViewableFacilitiesMap(waiterViewableFacilities);
    this.waiterViewableFacilitiesMap = waiterViewableFacilitiesMap;

    this.existRoomType = this.getExistRoomType(userStore.facility, waiterViewableFacilities);

    const facilityId: number = Number(this.$route.params.facilityid);
    const residentId: number = Number(this.$route.params.residentid);
    const idinsystem: number = Number(this.$route.params.idinsystem);
    const waiter: WaitersGetModel = await this.getWaiter(facilityId, residentId, idinsystem);
    this.waiter = waiter;

    this.admissionApplicationCreated.year = this.$dateUtil.convertNumberToString(waiter.waiter.admissionApplication.created, "yyyy", false);
    this.admissionApplicationCreated.month = this.$dateUtil.convertNumberToString(waiter.waiter.admissionApplication.created, "mm", false);
    this.admissionApplicationCreated.date = this.$dateUtil.convertNumberToString(waiter.waiter.admissionApplication.created, "dd", false);

    const waiterLabelMap: Map<number, number> = this.createWaiterLabelMap(waiter);
    const labels: Array<WaiterLabelsGetModel> = await this.getWaiterLabels();
    this.labels = this.createLabels(labels, waiterLabelMap);

    this.setRoomTypeAnswer(this.waiter.waiter.admissionApplication.templateAnswer.properties, waiterViewableFacilities);

    await this.setS3Files(this.waiter);
    this.initializeFileItems(this.waiter.waiter.admissionApplication.templateAnswer.properties);

    // itemのanswerにundefinedが格納されている場合がある不具合があったため、answerをチェックしてundefinedなら初期値を格納する
    this.chackAndInitializeAnswer(this.waiter.waiter.admissionApplication.templateAnswer.properties);

    this.waiterLoaded = true;

    this.isBusy = false;

    return;
  }

  /**
   * ### 閲覧可能施設を取得する
   */
  public async getWaiterViewableFacilities(userStore: LoginUser): Promise<Array<FacilityGetModel>> {
    const waiterViewableFacilities: Array<FacilityGetModel> = [];
    for (const facilityId of userStore.facility.waiterViewableFacilities) {
      const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: facilityId });
      waiterViewableFacilities.push(response);
    }
    return waiterViewableFacilities;
  }

  /**
   * ### 閲覧可能施設をMAP化する
   *
   * - key：施設ID、value：施設情報
   * - 自施設もMAPに含める
   */
  public createWaiterViewableFacilitiesMap(waiterViewableFacilities: Array<FacilityGetModel>): Map<number, FacilityGetModel> {
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();
    waiterViewableFacilitiesMap.set(userStore.facility.id, userStore.facility);
    for (const facility of waiterViewableFacilities) {
      waiterViewableFacilitiesMap.set(facility.id, facility);
    }
    return waiterViewableFacilitiesMap;
  }

  /**
   * ### 部屋種類が存在するかを返す
   */
  public getExistRoomType(mineFacility: FacilityGetModel, waiterViewableFacilities: Array<FacilityGetModel>): boolean {
    const mineFacilityExistRoomType: boolean = this.isRecruitmentRoom(mineFacility) || this.isCustomRequiredRecruitmentRoom(mineFacility);

    let waiterViewableFacilitiesEmptyRoomType: boolean = false;
    for (const viewableFacility of waiterViewableFacilities) {
      const existRoomType: boolean = this.isRecruitmentRoom(viewableFacility) || this.isCustomRequiredRecruitmentRoom(viewableFacility);
      waiterViewableFacilitiesEmptyRoomType = waiterViewableFacilitiesEmptyRoomType || existRoomType;
    }

    return mineFacilityExistRoomType || waiterViewableFacilitiesEmptyRoomType;
  }

  public isRecruitmentRoom(facility: FacilityGetModel): boolean {
    if (
      (facility.recruitmentRoom.multiBed ||
        facility.recruitmentRoom.conventinalSingle ||
        facility.recruitmentRoom.unitTypeSingle ||
        facility.recruitmentRoom.double ||
        facility.recruitmentRoom.single) == true
    ) {
      return true;
    } else {
      return false;
    }
  }

  public isCustomRequiredRecruitmentRoom(facility: FacilityGetModel): boolean {
    if (!facility.customRecruitmentRoom) {
      return false;
    }
    for (const room of facility.customRecruitmentRoom) {
      if (room.use == true) {
        return true;
      }
    }
    return false;
  }

  /**
   * ### 待機者を取得する
   */
  public async getWaiter(facilityId: number, residentId: number, idinsystem: number): Promise<WaitersGetModel> {
    const waitersByIdInSystem: Array<WaitersGetModel> = await this.waitersService.getsByIdInSystem(userStore, idinsystem);
    for (const waiter of waitersByIdInSystem) {
      if (waiter.facilityId == facilityId) {
        return waiter;
      }
    }
    // 見つからない場合は個別取得する
    const waiter: WaitersGetModel = await this.waitersService.get(userStore, facilityId, residentId);
    return waiter;
  }

  public getYearOptions(): Array<{ text: string; value: string | null }> {
    const options: Array<{ text: string; value: string | null }> = [{ text: "", value: null }]; // 初期値にリセット用の選択肢を用意しておく

    const currentYear: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyy");
    const startPosition: number = currentYear - 10;

    for (let i = 0; i <= 20; i++) {
      const year: number = startPosition + i;
      const date = new Date(year, 11, 31, 0, 0);
      const intlOptions: Intl.DateTimeFormatOptions = { year: "numeric" };
      const jpYear: string = new Intl.DateTimeFormat("ja-JP-u-ca-japanese", intlOptions).format(date).slice(0, -1); // 年が重複するので最終文字だけ削除;

      options.push({
        text: `${year}（${jpYear}）`,
        value: `${year}`
      });
    }

    return options;
  }

  public getMonthOptions(): Array<{ text: string; value: string | null }> {
    const options: Array<{ text: string; value: string | null }> = [{ text: "", value: null }]; // 初期値にリセット用の選択肢を用意しておく

    for (let i = 1; i <= 12; i++) {
      options.push({
        text: `${i}`,
        value: `${i}`
      });
    }

    return options;
  }

  public getDateOptions(): Array<{ text: string; value: string | null }> {
    const options: Array<{ text: string; value: string | null }> = [{ text: "", value: null }]; // 初期値にリセット用の選択肢を用意しておく

    for (let i = 1; i <= 31; i++) {
      options.push({
        text: `${i}`,
        value: `${i}`
      });
    }

    return options;
  }

  /**
   * ### 待機者ラベルMAPを作成する
   *
   * - key:ラベルID、value:ラベル
   */
  public createWaiterLabelMap(waiter: WaitersGetModel): Map<number, number> {
    let map: Map<number, number> = new Map<number, number>();
    if (waiter.waiter.labels != null) {
      for (const labelId of waiter.waiter.labels) {
        map.set(labelId, labelId);
      }
    }
    return map;
  }

  /**
   * ### 待機者ラベルを取得する
   */
  public async getWaiterLabels(): Promise<Array<WaiterLabelsGetModel>> {
    const response: Array<WaiterLabelsGetModel> = await this.waiterLabelsService.gets(userStore);
    return response;
  }

  /**
   * ### 待機者ラベルの初期値を作成する
   */
  public createLabels(labelMasters: Array<WaiterLabelsGetModel>, waiterLabelMap: Map<number, number>): Array<WaiterLabel> {
    let labels: Array<WaiterLabel> = [];
    for (const master of labelMasters) {
      const checked: boolean = waiterLabelMap.get(master.id) ? true : false;
      labels.push({
        ...master,
        checked: checked
      });
    }
    return labels;
  }

  /**
   * ### 部屋種類のanswer情報を格納する
   *
   * - 再帰呼び出しのため参照渡しする
   */
  public setRoomTypeAnswer(properties: Array<TemplateItem>, waiterViewableFacilities: Array<FacilityGetModel>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.setRoomTypeAnswer(item.properties, waiterViewableFacilities);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.setRoomTypeAnswer(childProperties, waiterViewableFacilities);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.setRoomTypeAnswer(childProperties.properties, waiterViewableFacilities);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.setRoomTypeAnswer(item.properties, waiterViewableFacilities);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.ROOM_TYPE) {
        let roomTypeItem: TemplateItemRoomType = inputItem as TemplateItemRoomType;
        roomTypeItem.answer = this.createRoomTypeCheckbox(roomTypeItem, waiterViewableFacilities);
      }
    }

    return;
  }

  /**
   * ### 部屋種類を再作成する
   *
   * - 部屋種類(固有)の再作成を行う
   *   - 自施設、閲覧可能施設の両方を設定対象とする
   * - 施設に部屋種類(固有)が存在する場合、部屋種類(固有)を作り直して設定する
   *   - 既存入力されている部屋種類(固有)をMAP化する
   *   - 施設の部屋種類(固有)の設定情報をもとに、MAPから前回入力値を復元する
   * - 施設に部屋種類(固有)が存在しない場合、部屋種類(固有)は空配列を設定する
   */
  public createRoomTypeCheckbox(item: TemplateItemRoomType, waiterViewableFacilities: Array<FacilityGetModel>): Array<RoomType> {
    for (const answer of item.answer) {
      // 自施設
      if (answer.facilityId == userStore.facility.id) {
        // 部屋種類(固有)の再作成
        let customRecruitmentRoomMap: Map<string, CustomRecruitmentRoom> = new Map<string, CustomRecruitmentRoom>();
        if (answer.customRecruitmentRoom) {
          for (const customRecruitmentRoom of answer.customRecruitmentRoom) {
            customRecruitmentRoomMap.set(customRecruitmentRoom.id, customRecruitmentRoom);
          }
        }

        let customRecruitmentRoom: Array<CustomRecruitmentRoom> = [];
        if (userStore.facility.customRecruitmentRoom) {
          for (const room of userStore.facility.customRecruitmentRoom) {
            const beforeCustomRecruitmentRoom: CustomRecruitmentRoom | undefined = customRecruitmentRoomMap.get(room.id);
            customRecruitmentRoom.push({
              id: room.id,
              name: room.name,
              abbreviationName: room.abbreviationName,
              color: room.color,
              use: beforeCustomRecruitmentRoom ? beforeCustomRecruitmentRoom.use : false
            });
          }
        }

        answer.customRecruitmentRoom = customRecruitmentRoom;
      }

      // 閲覧可能施設
      for (const viewableFacility of waiterViewableFacilities) {
        if (answer.facilityId == viewableFacility.id) {
          // 部屋種類(固有)の再作成
          let customRecruitmentRoomMap: Map<string, CustomRecruitmentRoom> = new Map<string, CustomRecruitmentRoom>();
          if (answer.customRecruitmentRoom) {
            for (const customRecruitmentRoom of answer.customRecruitmentRoom) {
              customRecruitmentRoomMap.set(customRecruitmentRoom.id, customRecruitmentRoom);
            }
          }

          let customRecruitmentRoom: Array<CustomRecruitmentRoom> = [];
          if (viewableFacility.customRecruitmentRoom) {
            for (const room of viewableFacility.customRecruitmentRoom) {
              const beforeCustomRecruitmentRoom: CustomRecruitmentRoom | undefined = customRecruitmentRoomMap.get(room.id);
              customRecruitmentRoom.push({
                id: room.id,
                name: room.name,
                abbreviationName: room.abbreviationName,
                color: room.color,
                use: beforeCustomRecruitmentRoom ? beforeCustomRecruitmentRoom.use : false
              });
            }
          }

          answer.customRecruitmentRoom = customRecruitmentRoom;
        }
      }
    }

    return item.answer;
  }

  /**
   * ### ファイルをS3から取得してFileオブジェクトにしてテンプレート情報に格納する
   */
  public async setS3Files(waiter: WaitersGetModel): Promise<void> {
    let fileItems: Array<TemplateItemFile> = [];
    this.searchFileItems(fileItems, waiter.waiter.admissionApplication.templateAnswer.properties);

    for (const fileItem of fileItems) {
      fileItem.tempAnswer = [];
      if (fileItem.answer != null) {
        for (const file of fileItem.answer) {
          if (file && file.url && file.name) {
            const s3File: File = await this.getS3File(file.url, file.name);
            fileItem.tempAnswer.push({
              file: s3File,
              fileName: file.name
            });
          }
        }
      }
    }

    return;
  }

  /**
   * ### Fileオブジェクトを探して返す
   */
  public searchFileItems(fileItems: Array<TemplateItemFile>, properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.searchFileItems(fileItems, item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.searchFileItems(fileItems, childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.searchFileItems(fileItems, childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.searchFileItems(fileItems, item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;
        if (fileItem.answer && 0 < fileItem.answer.length) {
          fileItems.push(fileItem);
        }
      }
    }

    return;
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  public async getS3File(url: string, fileName: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url, fileName);
    return response;
  }

  /**
   * ### ファイルの初期情報を作成する
   *
   * - 再帰呼び出しのため参照渡しする
   * - ファイルのanswerが空配列の場合、空のファイルを1つ追加する
   * - ファイルのanswerがnullの場合、空配列を作ってから空のファイルを1つ追加する
   */
  public initializeFileItems(properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.initializeFileItems(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.initializeFileItems(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.initializeFileItems(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.initializeFileItems(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;

        if (fileItem.answer == null) {
          fileItem.answer = [];
        }
        if (fileItem.tempAnswer == null) {
          fileItem.tempAnswer = [];
        }

        if (fileItem.tempAnswer.length <= 0) {
          fileItem.tempAnswer.push({
            file: null as any,
            fileName: ""
          });
        }
      }
    }
  }
  /**
   * ### admissionApplicationのanswerがundefinedの場合かをチェックし、undefinedなら初期値を格納する
   */
  private chackAndInitializeAnswer(properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;
          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.chackAndInitializeAnswer(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;
          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.chackAndInitializeAnswer(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;
          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.chackAndInitializeAnswer(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;
          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.chackAndInitializeAnswer(item.properties);
          }
        }
      }
      const inputItem: any = property as any;
      if (!inputItem.answer) {
        this.initializeAnswer(inputItem as TemplateItemInput);
      }
    }
  }

  /**
   * ### answerの初期値を格納する
   */
  private initializeAnswer(inputItem: TemplateItemInput): void {
    switch (inputItem.type) {
      case TemplateType.TEXT: {
        const item: TemplateItemText = inputItem as TemplateItemText;
        item.answer = "";
        break;
      }
      case TemplateType.RADIO: {
        const item: TemplateItemRadio = inputItem as TemplateItemRadio;
        item.answer = "";
        break;
      }
      case TemplateType.SELECT: {
        const item: TemplateItemSelect = inputItem as TemplateItemSelect;
        item.answer = [];
        break;
      }
      case TemplateType.CHECKBOX: {
        const item: TemplateItemCheckbox = inputItem as TemplateItemCheckbox;
        item.answer = false;
        break;
      }
      case TemplateType.MULTIPLE_CHECKBOX: {
        const item: TemplateItemMultipleCheckbox = inputItem as TemplateItemMultipleCheckbox;
        item.answer = [];
        break;
      }
      case TemplateType.TEXTAREA: {
        const item: TemplateItemTextarea = inputItem as TemplateItemTextarea;
        item.answer = "";
        break;
      }
      case TemplateType.ADDRESS: {
        const item: TemplateItemAddress = inputItem as TemplateItemAddress;
        item.answer = {
          address: "",
          postalCode: ""
        };
        break;
      }
      case TemplateType.BIRTHDAY: {
        const item: TemplateItemBirthday = inputItem as TemplateItemBirthday;
        item.answer = {
          year: "",
          month: "",
          date: ""
        };
        break;
      }
      case TemplateType.YEAR_MONTH_DATE: {
        const item: TemplateItemYearMonthDate = inputItem as TemplateItemYearMonthDate;
        item.answer = {
          year: "",
          month: "",
          date: ""
        };
        break;
      }
      case TemplateType.YEAR_MONTH: {
        const item: TemplateItemYearMonth = inputItem as TemplateItemYearMonth;
        item.answer = {
          year: "",
          month: ""
        };
        break;
      }
      case TemplateType.TEL: {
        const item: TemplateItemTel = inputItem as TemplateItemTel;
        item.answer = {
          first: "",
          second: "",
          third: ""
        };
        break;
      }
      case TemplateType.NAME: {
        const item: TemplateItemName = inputItem as TemplateItemName;
        item.answer = {
          firstName: "",
          secondName: "",
          firstKanaName: "",
          secondKanaName: ""
        };
        break;
      }
    }
    return;
  }

  /**
   * ### 更新確認モーダルを表示する
   *
   * - 以下バリデーションを行う
   * - 生年月日
   * - 部屋種類
   * - 各項目
   */
  public async showConfirmModal(residentStatus: ResidentStatus, updateType: string): Promise<void> {
    this.updateType = updateType;

    if (residentStatus == ResidentStatus.TEMPORARILY && updateType == "temp") {
      this.residentStatus = residentStatus;
      this.isTemporarilyButton = true;

      this.updateModalTitle = "入所申込者の情報を一時保存しますか？";
      this.showUpdateModal = true;
      return;
    }

    const validationResult: boolean = await this.$validator.validateAll();

    // 参照渡しだとエラー有無が上手く設定できないのでvalidInputErrorというクラス内のグローバルプロパティにエラー有無は設定する
    this.validInputError = true;
    if (this.waiter) {
      await this.validateBirthday(this.waiter.waiter.admissionApplication.templateAnswer.properties);
      await this.validateAdmissionApplicationCreated(this.admissionApplicationCreated);
      await this.validateRoomType(this.waiter.waiter.admissionApplication.templateAnswer.properties);
      await this.validateFile(this.waiter.waiter.admissionApplication.templateAnswer.properties);
    }

    if (validationResult && this.validInputError) {
      this.showValidationAlert = false;
      this.residentStatus = residentStatus;
      this.isTemporarilyButton = false;

      if (residentStatus == ResidentStatus.TEMPORARILY && updateType == "register") {
        this.updateModalTitle = "入所申込者の情報を登録しますか？";
      } else {
        this.updateModalTitle = "入所申込者の情報を変更しますか？";
      }
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 生年月日の存在するかしないかのバリデーション
   *
   * - 生年月日が1つでも入力されていればバリデーション対象として存在確認する
   * - 生年月日が1つも入力されていない場合はバリデーションせず、OKとする
   */
  public async validateBirthday(properties: Array<TemplateItem>): Promise<void> {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            await this.validateBirthday(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                await this.validateBirthday(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                await this.validateBirthday(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            await this.validateBirthday(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.BIRTHDAY) {
        const birthdayItem: TemplateItemBirthday = inputItem as TemplateItemBirthday;

        this.$validator.errors.remove(birthdayItem.id);

        if (birthdayItem.answer.year || birthdayItem.answer.month || birthdayItem.answer.date) {
          const birthday: number = this.joinBirthday(birthdayItem);
          const { valid, errors } = await this.$validator.verify(birthday, "exist_date", { name: "本項目", bails: false });

          if (!valid) {
            for (const error of errors) {
              this.$validator.errors.add({ field: birthdayItem.id, msg: error });
            }
            this.validInputError = false;
          } else {
            this.$validator.errors.remove(birthdayItem.id);
          }
        }
      }
    }
  }

  public async validateAdmissionApplicationCreated(admissionApplicationCreated: YearMonthDateAnswer): Promise<void> {
    if (admissionApplicationCreated.year || admissionApplicationCreated.month || admissionApplicationCreated.date) {
      const _admissionApplicationCreated: TemplateItemBirthday = {
        answer: admissionApplicationCreated
      } as TemplateItemBirthday;
      const date: number = this.joinBirthday(_admissionApplicationCreated);
      const { valid, errors } = await this.$validator.verify(date, "exist_date", { name: "本項目", bails: false });

      if (!valid) {
        for (const error of errors) {
          this.$validator.errors.add({ field: "created", msg: error });
        }
        this.validInputError = false;
      } else {
        this.$validator.errors.remove("created");
      }
    }
  }

  /**
   * ### propertiesを持つテンプレート要素かどうかを取得する
   */
  public hasProperties(item: TemplateItem): boolean {
    switch (item.type) {
      case TemplateType.SECTION:
      case TemplateType.GROUP:
      case TemplateType.RADIO_GROUP:
      case TemplateType.SWITCH_CHECKBOX: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  /**
   * ### 誕生日を結合する
   */
  public joinBirthday(birthdayItem: TemplateItemBirthday): number {
    let month: string = String(birthdayItem.answer.month);
    if (month.length == 1) {
      month = `0${month}`;
    }

    let date: string = String(birthdayItem.answer.date);
    if (date.length == 1) {
      date = `0${date}`;
    }

    const birthday: number = Number(`${birthdayItem.answer.year}${month}${date}000000000`);

    return birthday;
  }

  /**
   * ### 部屋種別のバリデーション
   */
  public async validateRoomType(properties: Array<TemplateItem>): Promise<void> {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            await this.validateRoomType(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                await this.validateRoomType(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                await this.validateRoomType(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            await this.validateRoomType(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.ROOM_TYPE) {
        const roomTypeItem: TemplateItemRoomType = inputItem as TemplateItemRoomType;

        let required: boolean = false;
        for (const validationRule of roomTypeItem.validationRules) {
          if (validationRule == "required") {
            required = true;
            break;
          }
        }

        if (required == true) {
          this.$validator.errors.remove(roomTypeItem.id);

          let isRoomInputTrue: boolean = false;
          let isCustomRoomInputTrue: boolean = false;
          for (const answer of roomTypeItem.answer) {
            isRoomInputTrue =
              answer.recruitmentRoom.multiBed ||
              answer.recruitmentRoom.conventinalSingle ||
              answer.recruitmentRoom.unitTypeSingle ||
              answer.recruitmentRoom.double ||
              answer.recruitmentRoom.single;

            if (answer.customRecruitmentRoom) {
              for (const customRoom of answer.customRecruitmentRoom) {
                if (customRoom.use == true) {
                  isCustomRoomInputTrue = true;
                  break;
                }
              }
            }

            if (isRoomInputTrue == true || isCustomRoomInputTrue == true) {
              break;
            }
          }

          if (isRoomInputTrue == false && isCustomRoomInputTrue == false) {
            this.$validator.errors.add({ field: roomTypeItem.id, msg: "本項目は必須入力です" });
            this.validInputError = false;
          } else {
            this.$validator.errors.remove(roomTypeItem.id);
          }
        }
      }
    }
  }

  /**
   * ### ファイルの10MBサイズバリデーション
   */
  public async validateFile(properties: Array<TemplateItem>): Promise<void> {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            await this.validateFile(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                await this.validateFile(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                await this.validateFile(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            await this.validateFile(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;

        if (fileItem.tempAnswer) {
          for (let i = 0; i < fileItem.tempAnswer.length; i++) {
            this.$validator.errors.remove(`${fileItem.id}-${i}`);

            if (fileItem.tempAnswer[i].file && 1024 * 1024 * 10 < fileItem.tempAnswer[i].file.size) {
              this.$validator.errors.add({ field: `${fileItem.id}-${i}`, msg: "本項目のファイルサイズが10MBを超えているため登録できません" });
              this.validInputError = false;
            } else {
              this.$validator.errors.remove(`${fileItem.id}-${i}`);
            }
          }
        }
      }
    }
  }

  /**
   * ### 待機者を更新する
   */
  public async updateWaiter(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const facilityId: number = Number(this.$route.params.facilityid);
      const residentId: number = Number(this.$route.params.residentid);
      const residentStatus: ResidentStatus | undefined = this.getResidentStatus(this.waiter as WaitersGetModel, this.isTemporarilyButton);
      const labelIds: number[] = this.createTargetLabelIds(this.labels);

      // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
      // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
      try {
        const requestBody: WaitersPatchModel = await this.createRequestBody(this.waiter as WaitersGetModel, residentStatus, labelIds);
        // 他施設の待機者を更新する場合は対象施設IDを格納する
        if (facilityId != userStore.loginUser.facilityId) {
          requestBody.targetFacilityId = facilityId;
        }
        await this.waitersService.patch(userStore, userStore.loginUser.facilityId, residentId, requestBody);
      } catch (error) {
        this.isBusy = false;

        this.showUploadErrorModal = true;
        const _waiter = this.waiter as WaitersGetModel;
        this.resetFileItems(_waiter.waiter.admissionApplication.templateAnswer.properties);

        return;
      }

      // 待機者一覧を再読み込みさせるようstoreを更新
      let waiterList: WaiterListState = needRefreshesStore.waiterList;
      waiterList.waiterList = true;
      needRefreshesStore.setWaiterList(waiterList);
      // メッセージ送信(待機者)の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendWaiterMessageState: SendWaiterMessagesState = needRefreshesStore.sendWaiterMessage;
      sendWaiterMessageState.sendMessages = true;
      needRefreshesStore.setSendWaiterMessage(sendWaiterMessageState);

      if (this.updateType == "temp") {
        this.successModalTitle = "一時保存に成功しました。";
      } else if (this.updateType == "register") {
        this.successModalTitle = "登録に成功しました。";
      } else {
        this.successModalTitle = "変更に成功しました。";
      }

      // サイドメニューを更新
      this.$emit("refresh");

      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 更新用の利用者状態を返す
   *
   * - 一時保存の待機者の場合、押下されたボタンに応じて変更後の利用者状態を変更する必要がある
   */
  public getResidentStatus(waiter: WaitersGetModel, isTemporarilyButton: boolean): ResidentStatus | undefined {
    if (isTemporarilyButton == false && waiter.status == ResidentStatus.TEMPORARILY) {
      return ResidentStatus.APPLICATION;
    } else {
      return undefined;
    }
  }

  /**
   * ### 待機者に付与するラベルIDを作成する
   */
  public createTargetLabelIds(waiterLabels: Array<WaiterLabel>): number[] {
    let labelIds: Array<number> = [];

    for (const label of waiterLabels) {
      if (label.checked == true) {
        labelIds.push(label.id);
      }
    }

    return labelIds;
  }

  /**
   * ### 待機者更新のリクエストボディを作成する
   */
  public async createRequestBody(waiter: WaitersGetModel, residentStatus: ResidentStatus | undefined, labelIds: number[]): Promise<WaitersPatchModel> {
    let fileItems: Array<TemplateItemFile> = [];
    this.searchFileItemsOnlyTempAnswer(fileItems, waiter.waiter.admissionApplication.templateAnswer.properties);

    // 初期値入力でtempAnswerに1つ空ファイルを格納しているので、実際に入力されたファイルを探す
    const files: Array<{ file: File; id: string }> = [];
    for (const item of fileItems) {
      if (item.tempAnswer != null) {
        for (let i = 0; i < item.tempAnswer.length; i++) {
          if (item.tempAnswer[i] && item.tempAnswer[i].file != null) {
            files.push({ file: item.tempAnswer[i].file, id: item.id + "-" + i });
          }
        }
      }
    }
    if (0 < files.length) {
      const fileMap: Map<string, { fileName: string; fileUrl: string }> = await this.uploadFiles(files);
      this.setFileTemplateToAnswer(fileMap, waiter.waiter.admissionApplication.templateAnswer.properties);
    }

    const _admissionApplicationCreated: TemplateItemBirthday = {
      answer: this.admissionApplicationCreated
    } as TemplateItemBirthday;
    const created: number = this.joinBirthday(_admissionApplicationCreated);
    waiter.waiter.admissionApplication.created = created;

    waiter.waiter.labels = labelIds;

    const requestBody: WaitersPatchModel = {
      waiter: waiter.waiter,
      updated: waiter.updated,
      updateType: UpdateType.WAITER_ADMISSION_APPLICATION
    };
    if (residentStatus) {
      requestBody.status = residentStatus;
    }

    return requestBody;
  }

  /**
   * ### Fileオブジェクトを探して返す
   */
  public searchFileItemsOnlyTempAnswer(fileItems: Array<TemplateItemFile>, properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.searchFileItemsOnlyTempAnswer(fileItems, item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.searchFileItemsOnlyTempAnswer(fileItems, childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.searchFileItemsOnlyTempAnswer(fileItems, childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.searchFileItemsOnlyTempAnswer(fileItems, item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;
        if (fileItem.tempAnswer && 0 < fileItem.tempAnswer.length) {
          fileItems.push(fileItem);
        }
      }
    }

    return;
  }

  /**
   * ### ファイルをアップロードする
   */
  public async uploadFiles(files: Array<{ file: File; id: string }>): Promise<Map<string, { fileName: string; fileUrl: string }>> {
    const fileMap: Map<string, { fileName: string; fileUrl: string }> = new Map<string, { fileName: string; fileUrl: string }>();

    try {
      // プログレス付きモーダルを表示する
      // 進捗はファイルのアプロード状況を反映させるようにする
      this.currentUploadCount = 0;
      this.uploadCount = files.length;
      this.progressMessage = "ファイルアップロード中。\n完了するまでそのままお待ちください。\nアップロード中に別画面に遷移しますと処理がキャンセルされます。";
      this.progressLoading = true;

      const urls: Array<S3LocationResponse> = await this.createUploadUrls(files, S3LocationType.ADMISSION_APPLICATION);
      for (let i = 0; i < urls.length; i++) {
        this.currentUploadCount = i;
        await this.s3UploadsService.uploadFile(urls[i], files[i].file);

        // DBにはファイルではなく、添付ファイルのS3URLを保持するためURL配列を作成する
        const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
        const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;

        fileMap.set(files[i].id, {
          fileName: files[i].file.name,
          fileUrl: s3Url
        });
      }
    } finally {
      this.progressLoading = false;
    }

    this.progressLoading = false;

    return fileMap;
  }

  /**
   * ### ファイルアップロード用のS3URLを作成する
   */
  public async createUploadUrls(files: Array<{ file: File; id: string }>, s3LocationType: S3LocationType): Promise<Array<S3LocationResponse>> {
    let extensions: Array<string> = [];

    for (const file of files) {
      switch (file.file.type) {
        case "image/jpeg": {
          extensions.push(".jpeg");
          break;
        }
        case "image/png": {
          extensions.push(".png");
          break;
        }
        case "video/mp4": {
          extensions.push(".mp4");
          break;
        }
        case "video/quicktime": {
          extensions.push(".mov");
          break;
        }
        case "application/pdf": {
          extensions.push(".pdf");
          break;
        }
      }
    }

    const requestBody: S3LocationRequest = {
      type: s3LocationType,
      count: files.length,
      extensions: extensions,
      params: {
        userId: userStore.loginUser.userId
      },
      acl: "public-read"
    };
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return url;
  }

  resetFileItems(properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.resetFileItems(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.resetFileItems(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.resetFileItems(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.resetFileItems(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;
        fileItem.tempAnswer = [];
      }
    }
    return;
  }

  /**
   * ### DB保存形式にファイル要素のAnswerを作成して格納する
   *
   * - 再帰呼び出しするので引数は参照渡し
   */
  public setFileTemplateToAnswer(fileMap: Map<string, { fileName: string; fileUrl: string }>, properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.setFileTemplateToAnswer(fileMap, item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.setFileTemplateToAnswer(fileMap, childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.setFileTemplateToAnswer(fileMap, childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.setFileTemplateToAnswer(fileMap, item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;

        fileItem.answer = [];
        if (fileItem.tempAnswer != null) {
          for (let i = 0; i < fileItem.tempAnswer.length; i++) {
            const s3File: { fileName: string; fileUrl: string } | undefined = fileMap.get(fileItem.id + "-" + i);
            if (s3File != undefined) {
              fileItem.answer.push({
                url: s3File.fileUrl,
                name: s3File.fileName
              });
            }
          }
        }

        fileItem.tempAnswer = [];
      }
    }
    return;
  }

  /**
   * ### 待機者詳細に遷移する
   */
  public gotoWaiterDetail(): void {
    const backType = Number(this.$route.params.backtype);
    if (backType == 1) {
      this.$router.push(PcUrl.WaiterList);
    } else if (backType == 2) {
      this.$router.push({
        name: PcUrl.WaiterDetail,
        params: { facilityid: this.$route.params.facilityid, idinsystem: this.$route.params.idinsystem, residentid: this.$route.params.residentid }
      });
    }
    return;
  }
}
interface WaiterLabel extends WaiterLabelsGetModel {
  /** チェック */
  checked: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#waiter-edit {
  max-width: 840px;
  margin-left: auto;
  margin-right: auto;

  .created,
  .labels {
    .title {
      font-weight: bold;
    }
  }

  .created {
    .year {
      width: 180px;
    }
    .month {
      width: 70px;
    }
    .date {
      width: 70px;
    }

    .validation-label {
      color: $validation-label-font-color;
      font-size: 10px;
      border: 1px solid $validation-label-font-color;
      padding: 1px 4px 0 4px;
      // 「必須」の文字が少し下にズレるでの、marginで調整する
      margin: 2px 6px 4px 0;
    }
    .noinput {
      background-color: $bg-require-input !important;
      &::placeholder {
        color: rgb(117, 117, 117);
      }
    }
  }

  .labels {
    .waiter-label {
      margin: 2px 0;
      font-size: 12px;
      padding: 4px;
      border-radius: 0.25rem;
    }
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
